import { notify } from '@affine/component';
import { useTrashDoc } from '@affine/core/components/hooks/affine/use-trash-doc';
import { useTrashFolderToast } from '@affine/core/components/hooks/affine/use-trash-folder-toast';
import { DocsService } from '@affine/core/modules/doc';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import { FolderMutationOutboxService } from '@affine/core/modules/folder-api';
import {
  moveFolderToTrashLocalFirst as moveFolderToTrashLocalFirstAction,
  type NavigationPanelFolderVisibility,
} from '@affine/core/modules/folder-api';
import {
  AccessibleFolderCacheStore,
  FolderStore,
} from '@affine/core/modules/organize';
import { AppBaseLifecycleOutboxService } from '@affine/core/modules/streamlain/services/app-base-lifecycle-outbox';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback } from 'react';

import type { NavigationPanelTreeSelectionItem } from './context';
import { filterNavigationPanelTrashableSelectionItems } from './selection-trash-capability';

function getFolderVisibility(item: NavigationPanelTreeSelectionItem) {
  return item.from?.at === 'navigation-panel:private:folder-node'
    ? 'private'
    : 'public';
}

export function useNavigationPanelTrashActions() {
  const t = useI18n();
  const docsService = useService(DocsService);
  const appLifecycle = useService(AppBaseLifecycleOutboxService);
  const workspace = useService(WorkspaceService).workspace;
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const folderMutationOutbox = useService(FolderMutationOutboxService);
  const folderStore = useService(FolderStore);
  const trashDoc = useTrashDoc();
  const showFolderTrashToast = useTrashFolderToast();
  const isCloudWorkspace = workspace.flavour !== 'local';

  const trashAppBase = useCallback(
    async (appBaseId: string) => {
      try {
        await appLifecycle.requestTrash(appBaseId);
        return true;
      } catch (error) {
        notify.error({
          title: t['com.streamlain.appBases.error.delete'](),
          message: error instanceof Error ? error.message : String(error),
        });
        return false;
      }
    },
    [appLifecycle, t]
  );

  const moveFolderToTrashLocalFirst = useCallback(
    (
      folderId: string,
      canTrashFolder: boolean,
      visibility: NavigationPanelFolderVisibility = 'public'
    ) => {
      return moveFolderToTrashLocalFirstAction({
        accessibleDocCache,
        accessibleFolderCache,
        folderMutationOutbox,
        folderId,
        folderStore,
        canTrashFolder,
        canUseYjsFolderRoute: !isCloudWorkspace,
        notifyMoveFailed: () => {
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.moveFailed'](),
          });
        },
        notifyRestoreFailed: () => {
          notify.error({
            title:
              t['com.streamlain.rootAppSidebar.privateFolder.restoreFailed'](),
          });
        },
        showFolderTrashToast,
        visibility,
        logError: console.error,
      });
    },
    [
      accessibleDocCache,
      accessibleFolderCache,
      folderMutationOutbox,
      folderStore,
      isCloudWorkspace,
      showFolderTrashToast,
      t,
    ]
  );

  const trashNavigationPanelItems = useCallback(
    async (items: NavigationPanelTreeSelectionItem[]) => {
      const trashableItems =
        filterNavigationPanelTrashableSelectionItems(items);
      const runTrash = async (
        itemsToTrash: NavigationPanelTreeSelectionItem[]
      ) => {
        for (const item of itemsToTrash) {
          if (item.appBaseTrash) {
            if (!(await trashAppBase(item.appBaseTrash.appBaseId))) {
              return false;
            }
            continue;
          }
          if (item.entity.type === 'folder') {
            const folderTrash = item.folderTrash;
            await moveFolderToTrashLocalFirst(
              item.entity.id,
              folderTrash?.canTrashFolder ?? false,
              getFolderVisibility(item)
            );
            continue;
          }

          const docRecord = docsService.list.doc$(item.entity.id).value;
          if (docRecord) {
            await trashDoc(docRecord);
          }
        }
        return true;
      };
      return await runTrash(trashableItems);
    },
    [docsService.list, moveFolderToTrashLocalFirst, trashDoc, trashAppBase]
  );

  return {
    moveFolderToTrashLocalFirst,
    trashAppBase,
    trashNavigationPanelItems,
  };
}
